import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Loader2, Check, Upload, Eye } from "lucide-react";
import { toast } from "sonner";
import { api, compressImageFile, shrinkGallery } from "../../lib/api";
import { SortableGallery } from "../../components/gallery/SortableGallery";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";

const STEPS = ["Basis", "Kenmerken", "Juridisch", "Presentatie", "Beschikbaarheid", "Preview"];
const TYPES = ["huis", "appartement", "studio", "penthouse", "villa", "bungalow", "overig"];
const ENERGY = ["A", "B", "C", "D", "E", "F", "G", "—"];

const initial = {
  title: "", street: "", house_number: "", house_number_addition: "", postcode: "", city: "", province: "",
  property_type: "huis", price: 0, price_condition: "kk", lat: null, lng: null,
  m2: 0, plot_m2: null, volume_m3: null, rooms: null, bedrooms: 0, bathrooms: null, floors: null, year_built: null,
  energy_label: "—", heating: "", balcony: false, garden: "", garage: false, parking: false, storage: false, elevator: false,
  ownership: "", leasehold: "", vve: false, vve_contribution: null, service_costs: null, monument: false, special_notes: "",
  description: "", features: [], image: null, gallery: [], floorplans: [], video_url: "",
  status: "concept", available_from: "In overleg", delivery: "", viewings_allowed: true,
};

const Field = ({ label, children }) => (<div><Label className="text-sm font-semibold text-navy">{label}</Label><div className="mt-1.5">{children}</div></div>);
const numOrNull = (v) => (v === "" || v === null || v === undefined ? null : Number(v));

const KoopWizard = () => {
  const { lid } = useParams();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [f, setF] = useState(initial);
  const [id, setId] = useState(lid || null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(!!lid);
  const dirty = useRef(false);
  const set = (k, v) => { dirty.current = true; setF((p) => ({ ...p, [k]: v })); };

  useEffect(() => {
    if (lid) {
      api(`/koop/makelaar/listings/${lid}`).then((d) => { setF({ ...initial, ...d, features: d.features || [], gallery: d.gallery || [] }); }).catch(() => toast.error("Kon woning niet laden")).finally(() => setLoading(false));
    }
  }, [lid]);

  const buildPayload = () => ({
    ...f,
    price: Number(f.price) || 0, m2: Number(f.m2) || 0, bedrooms: Number(f.bedrooms) || 0,
    plot_m2: numOrNull(f.plot_m2), volume_m3: numOrNull(f.volume_m3), rooms: numOrNull(f.rooms),
    bathrooms: numOrNull(f.bathrooms), floors: numOrNull(f.floors), year_built: numOrNull(f.year_built),
    vve_contribution: numOrNull(f.vve_contribution), service_costs: numOrNull(f.service_costs),
    image: f.image || (f.gallery && f.gallery[0]) || null,
  });

  const saveConcept = async (silent = false) => {
    if (!f.street.trim() || !f.city.trim()) { if (!silent) toast.error("Vul minimaal straat en plaats in om op te slaan."); return null; }
    setSaving(true);
    try {
      // Shrink oversized gallery/floorplan entries so the Mongo doc fits
      // under the 16 MB BSON limit.
      const [safeGallery, safeFloorplans] = await Promise.all([
        shrinkGallery(f.gallery || []),
        shrinkGallery(f.floorplans || []),
      ]);
      const payload = { ...buildPayload(), gallery: safeGallery, floorplans: safeFloorplans };
      payload.image = safeGallery[0] || null;
      let res;
      if (id) res = await api(`/koop/makelaar/listings/${id}`, { method: "PATCH", body: JSON.stringify(payload) });
      else { res = await api("/koop/makelaar/listings", { method: "POST", body: JSON.stringify(payload) }); setId(res.id); }
      setF((p) => ({ ...p, gallery: safeGallery, floorplans: safeFloorplans, image: safeGallery[0] || null }));
      dirty.current = false;
      if (!silent) toast.success("Concept opgeslagen");
      return res;
    } catch (e) { toast.error(e.message); return null; } finally { setSaving(false); }
  };

  const validateStep = () => {
    if (step === 0) {
      if (!f.street.trim() || !f.city.trim()) return "Straat en plaats zijn verplicht.";
      if (!f.price || Number(f.price) <= 0) return "Vul een vraagprijs in.";
    }
    return null;
  };

  const next = async () => {
    const err = validateStep();
    if (err) return toast.error(err);
    if (dirty.current) await saveConcept(true);
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
  };
  const prev = () => setStep((s) => Math.max(s - 1, 0));

  const uploadPhotos = async (files, target = "gallery") => {
    const current = f[target] || [];
    const room = Math.max(0, 40 - current.length);
    if (room === 0) { toast.error("Maximaal 40 foto's per advertentie."); return; }
    const arr = Array.from(files).slice(0, room);
    try {
      const encoded = await Promise.all(arr.map((file) => compressImageFile(file)));
      set(target, [...current, ...encoded]);
    } catch { toast.error("Uploaden mislukt"); }
  };
  const removePhoto = (i, target = "gallery") => set(target, (f[target] || []).filter((_, idx) => idx !== i));

  const publish = async () => {
    const res = await saveConcept(true);
    if (!res && !id) return;
    try {
      await api(`/koop/makelaar/listings/${id || res.id}/status`, { method: "PATCH", body: JSON.stringify({ status: "actief" }) });
      toast.success("Woning gepubliceerd!");
      navigate("/koop/makelaar");
    } catch (e) { toast.error(e.message); }
  };

  if (loading) return <div className="flex min-h-[50vh] items-center justify-center"><Loader2 className="animate-spin text-brandblue" /></div>;

  return (
    <div className="mx-auto max-w-3xl px-4 py-8 sm:px-6">
      <div className="flex items-center justify-between">
        <h1 className="font-display text-2xl font-extrabold text-navy">{id ? "Koopwoning bewerken" : "Nieuwe koopwoning"}</h1>
        <Button variant="outline" onClick={() => saveConcept(false)} disabled={saving} className="rounded-xl" data-testid="save-concept">
          {saving ? <Loader2 className="animate-spin" size={16} /> : "Concept opslaan"}
        </Button>
      </div>

      {/* Stepper */}
      <div className="mt-6 flex items-center gap-2 overflow-x-auto pb-2">
        {STEPS.map((s, i) => (
          <button key={s} onClick={() => i <= step && setStep(i)} className={`flex shrink-0 items-center gap-2 rounded-full px-3 py-1.5 text-xs font-bold ${i === step ? "bg-brandblue text-white" : i < step ? "bg-brandblue-light text-brandblue" : "bg-slate-100 text-slate-400"}`}>
            <span className="flex h-5 w-5 items-center justify-center rounded-full bg-white/20">{i < step ? <Check size={12} /> : i + 1}</span>{s}
          </button>
        ))}
      </div>

      <div className="mt-6 space-y-4 rounded-3xl border border-border bg-white p-6 shadow-sm">
        {step === 0 && (
          <>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Straat"><Input data-testid="wz-street" value={f.street} onChange={(e) => set("street", e.target.value)} className="rounded-xl" /></Field>
              <div className="grid grid-cols-2 gap-2">
                <Field label="Huisnr."><Input value={f.house_number} onChange={(e) => set("house_number", e.target.value)} className="rounded-xl" /></Field>
                <Field label="Toevoeging"><Input value={f.house_number_addition} onChange={(e) => set("house_number_addition", e.target.value)} className="rounded-xl" /></Field>
              </div>
              <Field label="Postcode"><Input value={f.postcode} onChange={(e) => set("postcode", e.target.value)} className="rounded-xl" /></Field>
              <Field label="Plaats"><Input data-testid="wz-city" value={f.city} onChange={(e) => set("city", e.target.value)} className="rounded-xl" /></Field>
              <Field label="Provincie"><Input value={f.province} onChange={(e) => set("province", e.target.value)} className="rounded-xl" /></Field>
              <Field label="Woningtype"><Select value={f.property_type} onValueChange={(v) => set("property_type", v)}><SelectTrigger className="rounded-xl"><SelectValue /></SelectTrigger><SelectContent>{TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select></Field>
              <Field label="Vraagprijs (€)"><Input data-testid="wz-price" type="number" value={f.price || ""} onChange={(e) => set("price", e.target.value)} className="rounded-xl" /></Field>
              <Field label="Prijsconditie"><Select value={f.price_condition} onValueChange={(v) => set("price_condition", v)}><SelectTrigger className="rounded-xl"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="kk">k.k.</SelectItem><SelectItem value="von">v.o.n.</SelectItem></SelectContent></Select></Field>
              <div className="grid grid-cols-2 gap-2">
                <Field label="Latitude"><Input type="number" value={f.lat ?? ""} onChange={(e) => set("lat", numOrNull(e.target.value))} className="rounded-xl" /></Field>
                <Field label="Longitude"><Input type="number" value={f.lng ?? ""} onChange={(e) => set("lng", numOrNull(e.target.value))} className="rounded-xl" /></Field>
              </div>
            </div>
          </>
        )}
        {step === 1 && (
          <div className="grid gap-4 sm:grid-cols-3">
            <Field label="Woonopp. (m²)"><Input type="number" value={f.m2 || ""} onChange={(e) => set("m2", e.target.value)} className="rounded-xl" /></Field>
            <Field label="Perceel (m²)"><Input type="number" value={f.plot_m2 ?? ""} onChange={(e) => set("plot_m2", e.target.value)} className="rounded-xl" /></Field>
            <Field label="Inhoud (m³)"><Input type="number" value={f.volume_m3 ?? ""} onChange={(e) => set("volume_m3", e.target.value)} className="rounded-xl" /></Field>
            <Field label="Kamers"><Input type="number" value={f.rooms ?? ""} onChange={(e) => set("rooms", e.target.value)} className="rounded-xl" /></Field>
            <Field label="Slaapkamers"><Input type="number" value={f.bedrooms || ""} onChange={(e) => set("bedrooms", e.target.value)} className="rounded-xl" /></Field>
            <Field label="Badkamers"><Input type="number" value={f.bathrooms ?? ""} onChange={(e) => set("bathrooms", e.target.value)} className="rounded-xl" /></Field>
            <Field label="Verdiepingen"><Input type="number" value={f.floors ?? ""} onChange={(e) => set("floors", e.target.value)} className="rounded-xl" /></Field>
            <Field label="Bouwjaar"><Input type="number" value={f.year_built ?? ""} onChange={(e) => set("year_built", e.target.value)} className="rounded-xl" /></Field>
            <Field label="Energielabel"><Select value={f.energy_label} onValueChange={(v) => set("energy_label", v)}><SelectTrigger className="rounded-xl"><SelectValue /></SelectTrigger><SelectContent>{ENERGY.map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)}</SelectContent></Select></Field>
            <Field label="Verwarming"><Input value={f.heating} onChange={(e) => set("heating", e.target.value)} className="rounded-xl" /></Field>
            <Field label="Tuin"><Input value={f.garden} onChange={(e) => set("garden", e.target.value)} placeholder="bijv. achtertuin" className="rounded-xl" /></Field>
            <div className="col-span-full grid grid-cols-2 gap-3 sm:grid-cols-4">
              {[["balcony", "Balkon"], ["garage", "Garage"], ["parking", "Parkeren"], ["storage", "Berging"], ["elevator", "Lift"]].map(([k, lbl]) => (
                <div key={k} className="flex items-center justify-between rounded-xl border border-border px-3 py-2"><span className="text-sm">{lbl}</span><Switch checked={f[k]} onCheckedChange={(v) => set(k, v)} /></div>
              ))}
            </div>
          </div>
        )}
        {step === 2 && (
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Eigendomssituatie"><Input value={f.ownership} onChange={(e) => set("ownership", e.target.value)} placeholder="volle eigendom / erfpacht" className="rounded-xl" /></Field>
            <Field label="Erfpacht (details)"><Input value={f.leasehold} onChange={(e) => set("leasehold", e.target.value)} className="rounded-xl" /></Field>
            <Field label="VvE-bijdrage p/m (€)"><Input type="number" value={f.vve_contribution ?? ""} onChange={(e) => set("vve_contribution", e.target.value)} className="rounded-xl" /></Field>
            <Field label="Servicekosten p/m (€)"><Input type="number" value={f.service_costs ?? ""} onChange={(e) => set("service_costs", e.target.value)} className="rounded-xl" /></Field>
            <div className="flex items-center justify-between rounded-xl border border-border px-3 py-2"><span className="text-sm">VvE aanwezig</span><Switch checked={f.vve} onCheckedChange={(v) => set("vve", v)} /></div>
            <div className="flex items-center justify-between rounded-xl border border-border px-3 py-2"><span className="text-sm">Monument</span><Switch checked={f.monument} onCheckedChange={(v) => set("monument", v)} /></div>
            <div className="sm:col-span-2"><Field label="Bijzonderheden"><Textarea value={f.special_notes} onChange={(e) => set("special_notes", e.target.value)} className="rounded-xl" /></Field></div>
          </div>
        )}
        {step === 3 && (
          <div className="space-y-4">
            <Field label="Titel"><Input data-testid="wz-title" value={f.title} onChange={(e) => set("title", e.target.value)} placeholder="bijv. Sfeervolle hoekwoning met tuin" className="rounded-xl" /></Field>
            <Field label="Omschrijving"><Textarea value={f.description} onChange={(e) => set("description", e.target.value)} className="min-h-[140px] rounded-xl" /></Field>
            <Field label="Kenmerken (komma-gescheiden)"><Input value={(f.features || []).join(", ")} onChange={(e) => set("features", e.target.value.split(",").map((x) => x.trim()).filter(Boolean))} className="rounded-xl" /></Field>
            <div>
              <Label className="text-sm font-semibold text-navy">Foto&apos;s ({f.gallery?.length || 0} / 40)</Label>
              <label className="mt-2 flex cursor-pointer items-center justify-center gap-2 rounded-xl border-2 border-dashed border-border py-6 text-sm text-slate-500 hover:border-brandblue">
                <Upload size={18} /> Sleep of klik om foto&apos;s te uploaden
                <input data-testid="wz-photos" type="file" accept="image/*" multiple className="hidden" onChange={(e) => uploadPhotos(e.target.files)} />
              </label>
              <div className="mt-3">
                <SortableGallery
                  value={f.gallery || []}
                  onChange={(next) => { set("gallery", next); set("image", next[0] || null); }}
                  onRemove={(i) => removePhoto(i, "gallery")}
                  data-testid="koop-gallery"
                />
              </div>
            </div>
            <Field label="Video-link (optioneel)"><Input value={f.video_url} onChange={(e) => set("video_url", e.target.value)} className="rounded-xl" /></Field>
          </div>
        )}
        {step === 4 && (
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Status"><Select value={f.status} onValueChange={(v) => set("status", v)}><SelectTrigger className="rounded-xl"><SelectValue /></SelectTrigger><SelectContent>
              <SelectItem value="concept">Concept</SelectItem><SelectItem value="actief">Actief</SelectItem><SelectItem value="onder_bod">Onder bod</SelectItem><SelectItem value="verkocht_ov">Verkocht o.v.</SelectItem><SelectItem value="verkocht">Verkocht</SelectItem><SelectItem value="ingetrokken">Ingetrokken</SelectItem>
            </SelectContent></Select></Field>
            <Field label="Beschikbaar vanaf"><Input value={f.available_from} onChange={(e) => set("available_from", e.target.value)} className="rounded-xl" /></Field>
            <Field label="Oplevering"><Input value={f.delivery} onChange={(e) => set("delivery", e.target.value)} className="rounded-xl" /></Field>
            <div className="flex items-center justify-between rounded-xl border border-border px-3 py-2"><span className="text-sm">Bezichtigingen toegestaan</span><Switch checked={f.viewings_allowed} onCheckedChange={(v) => set("viewings_allowed", v)} /></div>
          </div>
        )}
        {step === 5 && (
          <div data-testid="wz-preview">
            <div className="flex items-center gap-2 text-sm font-semibold text-brandblue"><Eye size={16} /> Zo ziet je woning er publiek uit</div>
            <div className="mt-4 overflow-hidden rounded-2xl border border-border">
              {f.gallery[0] && <img src={f.gallery[0]} alt="" className="aspect-[16/9] w-full object-cover" />}
              <div className="p-5">
                <div className="font-display text-2xl font-extrabold text-navy">{f.title || `${f.property_type} in ${f.city}`}</div>
                <div className="mt-1 text-slate-500">{f.street} {f.house_number}, {f.city}</div>
                <div className="mt-3 font-display text-xl font-bold text-navy">€ {Number(f.price || 0).toLocaleString("nl-NL")} {f.price_condition}</div>
                <div className="mt-3 flex flex-wrap gap-2 text-sm text-slate-600">
                  <span className="rounded-full bg-slate-50 px-3 py-1">{f.m2} m²</span>
                  {f.plot_m2 ? <span className="rounded-full bg-slate-50 px-3 py-1">{f.plot_m2} m² perceel</span> : null}
                  <span className="rounded-full bg-slate-50 px-3 py-1">{f.bedrooms} slaapk.</span>
                  {f.year_built ? <span className="rounded-full bg-slate-50 px-3 py-1">Bouwjaar {f.year_built}</span> : null}
                </div>
                {f.description && <p className="mt-3 whitespace-pre-line text-sm text-slate-600">{f.description}</p>}
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="mt-6 flex items-center justify-between">
        <Button variant="outline" onClick={prev} disabled={step === 0} className="rounded-xl">Vorige</Button>
        {step < STEPS.length - 1 ? (
          <Button data-testid="wz-next" onClick={next} className="rounded-xl bg-brandblue hover:bg-brandblue/90">Volgende</Button>
        ) : (
          <Button data-testid="wz-publish" onClick={publish} className="rounded-xl bg-brandblue px-8 hover:bg-brandblue/90">Publiceren</Button>
        )}
      </div>
    </div>
  );
};

export default KoopWizard;
